import { apiFetch, apiUpload, apiDownload } from './client';
import type {
  AuditLogRow,
  EmployeeDocument,
  EmployeeProfile,
  EmployeeProfileUpdate,
  HRFlagReport,
  HRInsightFeed,
  HROverview,
  HRPunchLocation,
  HRPersonFlagReport,
  PunchAlertPrefs,
  HRRequest,
  HRRequestStats,
  HRUser,
  HRUserAnalytics,
  PunchMode,
  ReportGroup,
  HRUserLeave,
  LeaveType,
  LeaveTypeBody,
  WorkZone
} from '$lib/types';

export interface HRInboxFilters {
  type?: string;
  status?: string;
  user_id?: string;
  page?: number;
  per_page?: number;
}

export interface HRRequestsPage {
  hr_requests: HRRequest[];
  page: number;
  per_page: number;
  total: number;
}

export interface HRManagedRequestBody {
  type: 'leave' | 'late' | 'early_leaving';
  start_date: string;
  end_date?: string;
  half_day?: boolean;
  reason_category?: HRRequest['reason_category'];
  reason: string;
  leave_type_id?: number;
}

function qs(filters: HRInboxFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

// hrInboxApi is the RequireHR-gated review surface at /api/hr/requests —
// visible to role hr and admin.
export const hrInboxApi = {
  list: (filters: HRInboxFilters) => apiFetch<HRRequestsPage>(`/hr/requests${qs(filters)}`),
  approve: (id: number) => apiFetch<HRRequest>(`/hr/requests/${id}/approve`, { method: 'POST' }),
  reject: (id: number) => apiFetch<HRRequest>(`/hr/requests/${id}/reject`, { method: 'POST' }),
  createForUser: (userId: number, body: HRManagedRequestBody) =>
    apiFetch<HRRequest>(`/hr/users/${userId}/requests`, { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: HRManagedRequestBody) =>
    apiFetch<HRRequest>(`/hr/requests/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  bulkApprove: (ids: number[]) =>
    apiFetch<{ count: number }>('/hr/requests/bulk_approve', {
      method: 'POST',
      body: JSON.stringify({ request_ids: ids })
    }),
  bulkReject: (ids: number[]) =>
    apiFetch<{ count: number }>('/hr/requests/bulk_reject', {
      method: 'POST',
      body: JSON.stringify({ request_ids: ids })
    }),
  stats: () => apiFetch<HRRequestStats>('/hr/requests/stats'),
  // setLeaveType backfills leave_type_id on a legacy/typeless pending leave
  // request (see HRSetRequestLeaveType) — only eligible before approval.
  setLeaveType: (id: number, leaveTypeId: number) =>
    apiFetch<HRRequest>(`/hr/requests/${id}/leave_type`, {
      method: 'PATCH',
      body: JSON.stringify({ leave_type_id: leaveTypeId })
    })
};

export interface WorkZoneBody {
  name: string;
  latitude: number;
  longitude: number;
  radius: number;
  active?: boolean;
  all_users?: boolean;
  description?: string;
  start_minute: number;
  end_minute: number;
  grace_minutes: number;
  google_maps_url?: string;
}

// hrWorkZonesApi is the RequireHR-gated site management surface at
// /api/hr/work_zones — moved from the old admin-only /api/admin/work_zones
// now that sites carry per-user assignments.
export const hrWorkZonesApi = {
  list: () => apiFetch<WorkZone[]>('/hr/work_zones'),
  create: (body: WorkZoneBody) =>
    apiFetch<WorkZone>('/hr/work_zones', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: WorkZoneBody) =>
    apiFetch<WorkZone>(`/hr/work_zones/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: number) => apiFetch<null>(`/hr/work_zones/${id}`, { method: 'DELETE' }),
  parseMapsUrl: (url: string) =>
    apiFetch<{ lat: number; lng: number }>('/hr/work_zones/parse_maps_url', {
      method: 'POST',
      body: JSON.stringify({ url })
    })
};

export interface HRCreateUserBody {
  name: string;
  email: string;
  role: string;
  password: string;
  punch_mode?: PunchMode;
}

export interface HRUpdateUserBody {
  report_group?: ReportGroup;
  name?: string;
  email?: string;
  role?: string;
  punch_mode?: PunchMode;
  active?: boolean;
}

// hrUsersApi backs both the Sites > Assignments view (list + per-user site
// assignment) and the HR > Users management view (create/edit/reset). The
// privilege matrix (who may do what) is enforced server-side — see
// internal/api/handlers/hr_users.go — the frontend just surfaces the 403s.
export const hrUsersApi = {
  list: () => apiFetch<HRUser[]>('/hr/users'),
  create: (body: HRCreateUserBody) => apiFetch<HRUser>('/hr/users', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: HRUpdateUserBody) =>
    apiFetch<HRUser>(`/hr/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  resetPassword: (id: number, password: string) =>
    apiFetch<{ detail: string }>(`/hr/users/${id}/reset_password`, {
      method: 'POST',
      body: JSON.stringify({ password })
    }),
  resetMFA: (id: number) => apiFetch<{ detail: string }>(`/hr/users/${id}/reset_mfa`, { method: 'POST' }),
  updateWorkZones: (userId: number, workZoneIds: number[]) =>
    apiFetch<HRUser>(`/hr/users/${userId}/work_zones`, {
      method: 'PUT',
      body: JSON.stringify({ work_zone_ids: workZoneIds })
    })
};

// hrOverviewApi backs HR > Overview (GET /api/hr/overview) — the org-wide
// snapshot landing tab. `date` (YYYY-MM-DD) picks the day; omit it for
// today. The server clamps a future date and resolves the calendar day in
// APP_TIMEZONE, echoing what it settled on back as HROverview.date.
export const hrOverviewApi = {
  get: (date?: string) => apiFetch<HROverview>(date ? `/hr/overview?date=${date}` : '/hr/overview'),
  punchLocation: (userId: number) => apiFetch<HRPunchLocation>(`/hr/users/${userId}/punch-location`),
  workLogLocation: (workLogId: number) => apiFetch<HRPunchLocation>(`/hr/attendance/work_logs/${workLogId}/location`)
};

export const hrAttendanceReportApi = {
  downloadPDF: (period: 'weekly' | 'monthly', date: string) =>
    apiDownload(`/hr/attendance/report.pdf?${new URLSearchParams({ period, date })}`, `attendance-${period}-${date}.pdf`),
  downloadLateReport: (from: string, to: string) =>
    apiDownload(`/hr/attendance/late-report?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, `late-report-${from}-${to}.csv`)
};

// hrPunchAlertsApi backs HR > Push alerts. Prefs are per caller; the test
// endpoint writes a bell row (and a Web Push if this device is subscribed).
export const hrPunchAlertsApi = {
  get: () => apiFetch<PunchAlertPrefs>('/hr/punch_alerts'),
  save: (body: Pick<PunchAlertPrefs, 'enabled' | 'scope' | 'events' | 'employee_ids'>) =>
    apiFetch<PunchAlertPrefs>('/hr/punch_alerts', { method: 'PUT', body: JSON.stringify(body) }),
  test: () => apiFetch<{ detail: string }>('/hr/punch_alerts/test', { method: 'POST' })
};

// hrInsightsApi backs Overview > Stats (GET /api/hr/insights). `range` is a
// filter chip, not a validated field: the server resolves anything it
// doesn't recognise to the 30d default rather than erroring, so a stale
// bookmark still renders a page.
export type HRInsightRangeKey = '7d' | '30d' | '90d' | '365d';

export const hrInsightsApi = {
  get: (range: HRInsightRangeKey) => apiFetch<HRInsightFeed>(`/hr/insights?range=${range}`)
};

// hrFlagsApi backs Overview > Flags (GET /api/hr/flags) — the per-person
// attendance-flags queue — and hrUserFlagsApi the same engine scoped to one
// person for the directory page's Flags section. Same range-chip contract as
// insights: the server resolves anything it doesn't recognise to 30d.
//
// Read-only by design. There is no mark-reviewed, no dismissal and no
// export: a flag exists while the behaviour does and disappears with it, so
// there is nothing to persist.
export const hrFlagsApi = {
  get: (range: HRInsightRangeKey) => apiFetch<HRFlagReport>(`/hr/flags?range=${range}`)
};

export const hrUserFlagsApi = {
  get: (userId: number, range: HRInsightRangeKey) =>
    apiFetch<HRPersonFlagReport>(`/hr/users/${userId}/flags?range=${range}`)
};

// hrAnalyticsApi backs the per-employee detail page (GET
// /api/hr/users/:id/analytics?days=N).
export const hrAnalyticsApi = {
  get: (userId: number, days: number) => apiFetch<HRUserAnalytics>(`/hr/users/${userId}/analytics?days=${days}`)
};

// hrProfileApi backs the employee detail page's Profile section (GET/PUT
// /api/hr/users/:id/profile) and its admin-only document upload/download/
// delete — see internal/api/handlers/employee_profile.go.
export const hrProfileApi = {
  get: (userId: number) => apiFetch<EmployeeProfile>(`/hr/users/${userId}/profile`),
  update: (userId: number, body: EmployeeProfileUpdate) =>
    apiFetch<EmployeeProfile>(`/hr/users/${userId}/profile`, { method: 'PUT', body: JSON.stringify(body) }),
  uploadDocument: (userId: number, file: File, label: string) => {
    const form = new FormData();
    form.set('file', file);
    if (label) form.set('label', label);
    return apiUpload<EmployeeDocument>(`/hr/users/${userId}/documents`, form);
  },
  downloadDocument: (userId: number, doc: EmployeeDocument) =>
    apiDownload(`/hr/users/${userId}/documents/${doc.id}`, doc.filename),
  deleteDocument: (userId: number, docId: number) =>
    apiFetch<{ detail: string }>(`/hr/users/${userId}/documents/${docId}`, { method: 'DELETE' })
};

// hrAttendanceApi backs the Attendance section's forgot-punch-out Fix
// action and the warning-counter Reset action (see hr_attendance.go).
export const hrAttendanceApi = {
  fixWorkLog: (workLogId: number, punchOut: string) =>
    apiFetch(`/hr/work_logs/${workLogId}/fix`, { method: 'POST', body: JSON.stringify({ punch_out: punchOut }) }),
  resetWarnings: (userId: number) => apiFetch<{ detail: string; reset_at: string }>(`/hr/users/${userId}/reset_warnings`, { method: 'POST' }),
  markPresent: (userId: number, body: { punch_in: string; punch_out?: string; reason: string; work_zone_id?: number }) =>
    apiFetch(`/hr/users/${userId}/mark_present`, { method: 'POST', body: JSON.stringify(body) })
};

// hrAuditApi backs the History section (per-employee, any hr/admin caller)
// and the admin-only org-wide Audit page (see audit.go).
export const hrAuditApi = {
  userAudit: (userId: number) => apiFetch<AuditLogRow[]>(`/hr/users/${userId}/audit`),
  adminList: (filters: { user_id?: string; action?: string; limit?: number; offset?: number } = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') params.set(key, String(value));
    }
    const s = params.toString();
    return apiFetch<AuditLogRow[]>(`/admin/audit${s ? `?${s}` : ''}`);
  }
};

// hrLeaveApi backs Manage > Leave types (admin CRUD, hr read-only) and the
// employee detail page's Leave section (summary + adjust) — see leave.go.
export const hrLeaveApi = {
  listTypes: () => apiFetch<LeaveType[]>('/hr/leave_types'),
  createType: (body: LeaveTypeBody) => apiFetch<LeaveType>('/hr/leave_types', { method: 'POST', body: JSON.stringify(body) }),
  updateType: (id: number, body: LeaveTypeBody) =>
    apiFetch<LeaveType>(`/hr/leave_types/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteType: (id: number) => apiFetch<null>(`/hr/leave_types/${id}`, { method: 'DELETE' }),
  userLeave: (userId: number) => apiFetch<HRUserLeave>(`/hr/users/${userId}/leave`),
  backfill: (entry: { user_id: number; leave_type_id: number; days: number; note?: string }) =>
    apiFetch<{ created: number }>('/hr/leave/backfill', {
      method: 'POST',
      body: JSON.stringify({ entries: [entry] })
    }),
  adjust: (userId: number, body: { leave_type_id: number; days: number; note: string }) =>
    apiFetch<HRUserLeave>(`/hr/users/${userId}/leave/adjust`, { method: 'POST', body: JSON.stringify(body) })
};
